import { TableModalWithLayoutSettings } from '@Components/Tables/TableModalWithLayoutSettings';
import { contactColumns } from '@Constants/TableColumns';
import { type ApiContact, EnumFrontendLayoutSetting } from '@Model';
import { Awaitable, Button, ListInfo, ListRequest, Modal, Progress } from '@synqo/frontend-component-library';
import { useMemo } from 'react';

interface ImportableErpContactsModalProps {
    isOpen: boolean;
    title: string;
    loadContacts: (lr: ListRequest) => Awaitable<ListInfo<ApiContact>>;
    isImporting: boolean;
    handleImport: (selectedContactIds: string[]) => Promise<void> | void;
    onClose: () => void;
    onBack?: () => void;
}

export function ImportableErpContactsModal({
    isOpen,
    title,
    loadContacts,
    isImporting,
    handleImport,
    onClose,
    onBack,
}: ImportableErpContactsModalProps) {
    const actions = useMemo(
        () => [
            {
                name: 'import',
                label: isImporting ? 'Wird importiert...' : 'Importieren',
                action: handleImport,
            },
        ],
        [handleImport, isImporting],
    );

    const toolBarElements = useMemo(() => {
        if (!onBack) {
            return [];
        }
        const backButton = (
            <Button key="back" onClick={onBack}>
                Zurück
            </Button>
        );
        return [backButton];
    }, [onBack]);

    if (isImporting) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} title={title} bodyProps={{ type: 'never' }} withoutCloseButton>
                <Progress id={'contact-import-progress-bar'} animated size="md" ariaLabel="Import is running..." />
            </Modal>
        );
    }

    return (
        <TableModalWithLayoutSettings<ApiContact>
            title={title}
            isOpen={isOpen}
            onClose={onClose}
            layoutSettingsContext={EnumFrontendLayoutSetting.ContactsTableLayout}
            multiple
            tableProps={{
                name: 'ImportableErpContactsList',
                columns: contactColumns,
                loader: loadContacts,
                toolbarElements: toolBarElements,
            }}
            actions={actions}
        />
    );
}
